<template>
    <component :is="wrapperElement" v-bind="$attrs" @focusout="focusout" ref="wrapper">
        <slot v-bind="{open, close, toggle, mouseenter, mouseleave, isOpen: popoverVisible, popoverId}" name="default"></slot>
        <div v-if="showBackground && popoverVisible" v-interact="close"
            class="popover-overlay" style=""></div>
        <div class="popover-container" v-if="popoverVisible" ref="container" :style="containerStyle" :class="[{padding: containerPadding}, containerClass]"
            :aria-labelledby="useLabel ? popoverId : ''" aria-live="assertive">
            <div class="popover-arrow" v-if="arrow" ref="arrowRef"></div>
            <div v-if="focusTrap" @focus="focusLastElement" tabindex="0" class="popup-focus-trap"/>
            <slot name="container" v-bind="{close, popoverId}"></slot>
            <div v-if="focusTrap" @focus="focusFirstElement" tabindex="0" class="popup-focus-trap"/>
        </div>
    </component>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import {getOuterDimensions, isParent, setAlignment, setStyles} from '@/library/dom';
    import {getKeyboardFocusableElements, queryFocusableElements} from '@/library/focus';
    import {uniqueId} from '@/library/string';
    import {identifyElement} from '@/library/utils';
    import {isDeveloper} from '@/utils/portuna';
    import {ref, nextTick, onUpdated, onMounted, onBeforeUnmount, watch, computed} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        open: [];
        close: [];
        mouseenter: [];
        mouseleave: [];
    }>();

    const props = withDefaults(
        defineProps<{
            value?: boolean;
            position?: AlignmentPosition;
            target?: string | HTMLElement;
            wrapperElement?: string;
            margin?: number;
            inheritWidth?: boolean | 'at-least';
            contentWidth?: boolean;
            closeOnScroll?: boolean;
            arrow?: boolean;
            containerPadding?: boolean;
            containerClass?: string;
            maxHeight?: number;
            maxWidth?: number;
            preventHeaderOverlap?: boolean;
            handleFocusable?: boolean;
            focusTrap?: boolean;
            autoFocusFirstElement?: boolean;
            useLabel?: boolean;
            containerStyle?: object;
            showBackground?: boolean;
        }>(),
        {
            position: 'bottom',
            target: '',
            wrapperElement: 'div',
            margin: 0,
            inheritWidth: false,
            contentWidth: false,
            closeOnScroll: true,
            arrow: false,
            containerPadding: false,
            containerClass: '',
            maxHeight: 0,
            maxWidth: 0,
            preventHeaderOverlap: false,
            handleFocusable: true,
            focusTrap: true,
            autoFocusFirstElement: false,
            useLabel: false,
            showBackground: false,
            containerStyle: () => ({}),
        },
    );

    const slots = defineSlots<{
        default(props: {
            open: (event: MouseEvent | FocusEvent | KeyboardEvent | PointerEvent | null) => void
            close: () => void
            toggle: () => void
            mouseenter: () => void
            mouseleave: () => void
            isOpen: boolean
            popoverId: string
        }): any
        container(props: { close: () => void; popoverId: string }): any
    }>();

    const arrowRef  = ref<HTMLElement>();
    const container = ref<HTMLElement>();
    const wrapper   = ref<HTMLElement>();

    const wrapperElementProxy = computed<Nullable<HTMLElement>>(() =>
    {
        const raw = wrapper.value;
        if (!(raw instanceof HTMLElement))
        {
            const elementByVue = identifyElement<HTMLElement>(raw);
            if (elementByVue)
            {
                return elementByVue;
            }

            console.error('Couldn\'t find any element. Followup issues incomming!');

            return null;
        }

        return raw;
    });

    const mouseover         = ref(false);
    const timeout           = ref<Nullable<number>>(0);
    const focusInterval     = ref<number>(0);
    const popoverVisible    = ref<boolean>(false);
    const popoverId         = `popover_${uniqueId()}`;
    const focusableElements = ref<Nullable<NodeListOf<HTMLElement>>>(null);

    const focusFirstElement = () =>
    {
        if (focusableElements.value && focusableElements.value[0])
        {
            focusableElements.value[0].focus();
        }
    };

    const focusLastElement = () =>
    {
        if (focusableElements.value && focusableElements.value[focusableElements.value.length - 1])
        {
            focusableElements.value[focusableElements.value.length - 1].focus();
        }
    };

    const queryContainerFocusableElements = () =>
    {
        if (container.value)
        {
            focusableElements.value = queryFocusableElements(container.value);
        }
    };

    const moveToMouseEvent = (event: MouseEvent) =>
    {
        setStyles(container.value!, {
            left: event.clientX + 'px',
            top: event.clientY + 'px',
            right: 'auto',
            bottom: 'auto',
        });
    };

    const clearTimeout = () =>
    {
        if (timeout.value)
        {
            window.clearTimeout(timeout.value);

            timeout.value = null;
        }
    };

    const mouseenter = () =>
    {
        mouseover.value = true;

        clearTimeout();

        timeout.value = window.setTimeout(() =>
        {
            // check if mouse is over the element before xhr
            if (!mouseover.value)
            {
                return;
            }

            emit('mouseenter');

            portuna.emitter.emit('mouseenter');
        }, 100);
    };

    const refresh = (event: Nullable<MouseEvent | Event> = null) =>
    {
        if (!popoverVisible.value)
        {
            return;
        }

        let position = props.position;
        if ('cursor' === position)
        {
            if (
                event instanceof MouseEvent &&
                'undefined' !== typeof event.clientX &&
                'undefined' !== typeof event.clientY
            )
            {
                moveToMouseEvent(event);

                return;
            }
            else
            {
                position = 'bottom';
            }
        }

        let targetElement: HTMLElement =
                (typeof props.target === 'string'
                    ? props.target && document.querySelector(props.target)
                    : props.target) || wrapperElementProxy.value!;

        // return if the container is not available
        if (!container.value)
        {
            return;
        }

        // reset styles
        let properties = ['max-height', 'border-width', 'border-top-color', 'display'];
        if (props.inheritWidth)
        {
            properties.push('at-least' === props.inheritWidth ? 'min-width' : 'width');
        }

        if (container.value!.style)
        {
            for (let property of properties)
            {
                container.value!.style.removeProperty(property);
            }

            if (props.inheritWidth)
            {
                container.value!.style.setProperty(
                    'at-least' === props.inheritWidth ? 'min-width' : 'width',
                    targetElement.clientWidth + 'px',
                );
            }

            if (props.contentWidth)
            {
                const minWidth = getOuterDimensions(container.value!).width + 20;

                container.value!.style.minWidth = (minWidth > 700 ? 700 : minWidth) + 'px';
                container.value!.style.maxWidth = '700px';
            }

            if (props.maxHeight)
            {
                container.value!.style.maxHeight = Math.min(getOuterDimensions(container.value!).height, props.maxHeight) + 'px';
            }

            if (props.maxWidth)
            {
                container.value!.style.maxWidth = Math.min(getOuterDimensions(container.value!).width, props.maxWidth) + 'px';
            }
        }

        setAlignment(container.value!, targetElement, {
            forcePosition: false,
            fixed: true,
            keepOnScreen: false,
            position: position,
            arrow: props.arrow,
            preventHeaderOverlap: props.preventHeaderOverlap,
        });
    };

    onUpdated(() =>
    {
        refresh();

        nextTick(() => props.value && queryContainerFocusableElements());
    });

    onMounted(() =>
    {
        if (props.handleFocusable)
        {
            nextTick(() =>
            {
                try
                {
                    const keyboardFocusableElements = getKeyboardFocusableElements(wrapperElementProxy.value!);
                    let addEventElement             = wrapperElementProxy.value!;

                    if (keyboardFocusableElements.length)
                    {
                        addEventElement = keyboardFocusableElements[0];
                    }
                    else
                    {
                        wrapperElementProxy.value!.setAttribute('tabindex', '0');
                    }

                    addEventElement.addEventListener('keydown', (event: KeyboardEvent) =>
                    {
                        if ('Space' === event.code)
                        {
                            event.preventDefault();

                            event.stopPropagation();
                        }
                    });

                    addEventElement.addEventListener('keyup', (event: KeyboardEvent) =>
                    {
                        if (['Space', 'Enter'].includes(event.code))
                        {
                            event.preventDefault();

                            event.stopPropagation();

                            open(event);
                        }
                    });
                }
                catch (e)
                {
                    console.log(e);
                    console.log(wrapperElementProxy.value);
                }
            });
        }
    });

    onBeforeUnmount(() =>
    {
        close();
    });

    const eventClose = () =>
    {
        close();
    };

    const eventClick = (event: any) =>
    {
        let parent = event.target.parentElement;
        while (parent)
        {
            if (parent.parentElement === document.body)
            {
                break;
            }

            parent = parent.parentElement;
        }

        if (container.value && !isParent(wrapperElementProxy.value!, event.target) && parent !== null)
        {
            close();
        }
    };

    const eventKeyup = (event: KeyboardEvent) =>
    {
        if (event.code === 'Escape')
        {
            event.preventDefault();

            close();
        }
    };

    const open = (event: Nullable<MouseEvent | FocusEvent | KeyboardEvent | PointerEvent> = null) =>
    {
        if (popoverVisible.value)
        {
            return;
        }

        popoverVisible.value = true;

        if (typeof slots.container === 'undefined')
        {
            if (isDeveloper())
            {
                throw new Error('no container found');
            }

            return;
        }

        setTimeout(() =>
        {
            window.addEventListener('keydown', eventKeyup);
            window.addEventListener('click', eventClick); // left click
            window.addEventListener('touchstart', eventClick); // left click
            window.addEventListener('contextmenu', eventClick); // right click

            if (props.closeOnScroll)
            {
                window.addEventListener('scroll', eventClose);

                window.addEventListener('resize', eventClose);
            }
            else
            {
                window.addEventListener('scroll', refresh, true);

                window.addEventListener('resize', refresh);
            }
        }, 1);

        nextTick(() =>
        {
            queryContainerFocusableElements();
        });

        if (event && event instanceof KeyboardEvent && props.autoFocusFirstElement)
        {
            nextTick(() =>
            {
                const keyboardFocusableElements = getKeyboardFocusableElements(container.value);

                window.setTimeout(() =>
                {
                    keyboardFocusableElements[0] && keyboardFocusableElements[0].focus();
                }, 50);

                for (let el of keyboardFocusableElements)
                {
                    el.addEventListener('keydown', (event: KeyboardEvent) =>
                    {
                        'Space' === event.code && event.preventDefault();
                    });
                }
            });
        }

        if (!focusInterval.value)
        {
            focusInterval.value = window.setInterval(() =>
            {
                if (!wrapperElementProxy.value)
                {
                    return clearInterval(focusInterval.value);
                }

                if (wrapperElementProxy.value!.contains(document.activeElement))
                {
                    return;
                }

                // window-click not fired on iframe-click
                if (document.activeElement instanceof HTMLIFrameElement)
                {
                    close();
                }
            }, 200);
        }

        nextTick(() =>
        {
            refresh(event);

            emit('open');

            portuna.emitter.emit('open');
        });
    };

    const close = () =>
    {
        if (focusInterval.value)
        {
            window.clearInterval(focusInterval.value);
            focusInterval.value = 0;
        }

        window.removeEventListener('keydown', eventKeyup);
        window.removeEventListener('resize', eventClose);
        window.removeEventListener('scroll', eventClose);
        window.removeEventListener('scroll', refresh, true);
        window.removeEventListener('resize', refresh);
        window.removeEventListener('click', eventClick);
        window.removeEventListener('touchstart', eventClick);

        if (!popoverVisible.value)
        {
            return;
        }

        if (props.autoFocusFirstElement)
        {
            wrapperElementProxy.value!.focus();
        }

        popoverVisible.value = false;

        emit('close');

        portuna.emitter.emit('close');
    };

    const toggle = (event: Nullable<FocusEvent | KeyboardEvent | PointerEvent> = null) =>
    {
        if (popoverVisible.value)
        {
            close();
        }
        else
        {
            open(event);
        }
    };

    const mouseleave = (addContainerEvents = true) =>
    {
        clearTimeout();

        const containerInstance  = container.value as HTMLDivElement;
        const mouseenter         = () => clearTimeout();
        const mouseleaveInternal = () =>
        {
            containerInstance.removeEventListener('mouseenter', mouseenter);

            containerInstance.removeEventListener('mouseleave', mouseleaveInternal);

            mouseleave(false);
        };

        timeout.value = window.setTimeout(() =>
        {
            if (containerInstance)
            {
                containerInstance.removeEventListener('mouseenter', mouseenter);
                containerInstance.removeEventListener('mouseleave', mouseleaveInternal);
            }

            mouseover.value = false;

            close();

            emit('mouseleave');

            portuna.emitter.emit('mouseleave');
        }, 200);

        // if the container is available add mouseenter/mouseleave events
        if (containerInstance && addContainerEvents)
        {
            containerInstance.addEventListener('mouseenter', mouseenter);
            containerInstance.addEventListener('mouseleave', mouseleaveInternal);
        }
    };

    const focusout = (e: FocusEvent) =>
    {
        if (!wrapperElementProxy.value?.contains(e.target as Node))
        {
            close();
        }
    };

    watch(() => props.value, (newValue: boolean) =>
    {
        popoverVisible.value = newValue;

        nextTick(() =>
        {
            if (newValue)
            {
                open();
            }
            else
            {
                close();
            }
        });
    });

    defineExpose(Object.assign({}, portuna, {
        componentName: 'AppPopover',
        focusout,
        mouseleave,
        toggle,
        close,
        open,
        eventKeyup,
        eventClick,
        eventClose,
        refresh,
        mouseenter,
        clearTimeout,
        moveToMouseEvent,
        queryContainerFocusableElements,
        focusLastElement,
        focusFirstElement,
        mouseover,
        timeout,
        focusInterval,
        popoverVisible,
        popoverId,
        focusableElements,
        arrow: arrowRef,
        container: container,
        wrapper: wrapperElementProxy,
    }));
</script>